import {FieldPath, firestore} from '@src/db';
import {TransactionEntity, TransactionFragment} from '@entities/TransactionEntity';

import {apolloClient} from '@src/network/apollo/apolloClient';
import {gqlWithRetry} from '@repositories/GqlRetry';
import {BillingActions, TransferMode, TaxIdentifier, ExternalINSSType, FinancialPersonType, FinancialItemsTypes, FinancialItemsPaymentStatus, FinancialDeductionPoint} from '@common/status';

import gql from 'graphql-tag';
import NumberUtils from '@common/NumberUtils';


/**
 *
 * @param {String} companyId
 * @param {String[]} ids
 * @param {Date=} dateBegin
 * @param {Date=} dateEnd
 * @param mode
 * @param actions
 * @returns {Promise<any[]>}
 */
export async function getTransactionsOpenByCobrancasIds(companyId, ids, dateBegin=null, dateEnd=null, mode=TransferMode.ALL, actions=[BillingActions.Received]) {
  const where = {
    'cobrancaId': {
      'in': ids
    },
    'AND': [
      {
        'transferId': {
          'equals': null
        }
      },
      {
        'canceledId': {
          'equals': null
        }
      },
      {
        'canceledById': {
          'equals': null
        }
      }
    ]
  };
  if(dateBegin) {
    where.AND.push({
      'creationDate': {
        'gte': dateBegin
      }
    });
  }
  if(dateEnd) {
    where.AND.push({
      'creationDate': {
        'lt': dateEnd
      }
    });
  }
  if(actions) {
    where.AND.push({
      'action': {
        'in': actions
      }
    });
  }
  if(mode===TransferMode.RECEIVED) {
    where.AND.push({
      'appealId': {
        'equals': null
      }
    });
  } else if(mode===TransferMode.RECURSED) {
    where.AND.push({
      'appealId': {
        'not': null
      }
    });
  }

  const result = await gqlWithRetry('getTransactionsOpenByCobrancasIds', () => apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: where
    },
  }));
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

/**
 * Transações já vinculadas a um repasse (transferId preenchido no Postgres).
 * `actions` default mantém o comportamento histórico (só Received); o resgate do
 * fechamento parcial no TransferEdit passa também ReceivedFromOver, alinhado ao
 * getTransactionsOpenByCobrancasIds.
 * @param transferId
 * @param mode
 * @param {String[]=} actions
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getTransactionsByTransferId(transferId, mode=TransferMode.ALL, actions=[BillingActions.Received]) {
  const where = {
    'transferId': {
      'equals': transferId
    },
    'canceledId': {
      'equals': null
    },
    'canceledById': {
      'equals': null
    },
    'action': {
      'in': actions
    }
  };
  if(mode===TransferMode.RECEIVED) {
    where.appealId = {
      'equals': null
    };
  } else if(mode===TransferMode.RECURSED) {
    where.appealId = {
      'not': null
    };
  }

  const result = await gqlWithRetry('getTransactionsByTransferId', () => apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: where
    },
  }));
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

/**
 *
 * @param eventId
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getTransactionsByEventId(eventId) {
  if(!eventId) {
    console.error('eventId not found');
    return [];
  }
  const result = await apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: {
        'eventId': {
          'equals': eventId
        }
      }
    },
  });
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}
/**
 *
 * @param eventIds
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getTransactionsByEventIds(eventIds) {
  if(!eventIds?.length) {
    console.error('eventId not found');
    return [];
  }
  const result = await apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: {
        'eventId': {
          'in': eventIds
        }
      }
    },
  });
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}



/**
 * old getTransactionByProps
 * @returns {Promise<TransactionEntity[]>}
 * @param eventId
 * @param type
 */
export async function getActivesTransactionsByEventIdAndType(eventId, type) {
  const result = await apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: {
        'eventId': {
          'equals': eventId
        },
        'AND': [
          {
            'type': {
              'equals': type
            }
          },
          {
            'canceledId': {
              'equals': null
            }
          },
          {
            'canceledById': {
              'equals': null
            }
          }
        ],
      }
    },
  });
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

// Limite do operador `in` do Firestore (mesmo do fcmGetByPropIds).
const FIRESTORE_IN_LIMIT = 30;

/**
 * Igual ao `fcmGetByPropIds` do FcmFirestore, mas com os chunks de 30 ids em PARALELO.
 *
 * O `fcmGetByPropIds` percorre os chunks em série (`for` com `await`), e o FcmFirestore é
 * arquivo gerado (`src/libs/FcmFirestore` — "não edite diretamente"), então a paralelização
 * fica no chamador. No recálculo do repasse isso pesa: `getPreviouslyRetained` varre os
 * `financialItems` do mês de **todos** os recebedores — com 200 cooperados são 7 round-trips
 * enfileirados para uma leitura que é naturalmente paralela.
 *
 * @param {function(): Object} makeRef fábrica da query base (chamada uma vez por chunk)
 * @param {String|Object} prop campo do filtro
 * @param {String[]} ids
 * @returns {Promise<Object[]>}
 */
async function fcmGetByPropIdsParallel(makeRef, prop, ids) {
  const chunks = [];
  for (let i = 0; i < (ids?.length || 0); i += FIRESTORE_IN_LIMIT) {
    chunks.push(ids.slice(i, i + FIRESTORE_IN_LIMIT));
  }
  const snapshots = await Promise.all(chunks.map((idsChunk)=>makeRef().where(prop, 'in', idsChunk).get()));
  const result = [];
  for (const snap of snapshots) {
    for (const doc of snap.docs) {
      result.push({
        ...doc.data(),
        id: doc.id
      });
    }
  }
  return result;
}

/**
 * Receitas EM ABERTO com desconto em folha pré-nota (`deductionPoint: NOTA`) dos recebedores do
 * repasse — a fonte do abatimento do encontro de contas (#1111, fase 2b).
 *
 * Regras (decisões da #1111, ver docs/financial/FechamentoFinanceiro.md):
 * - **sem corte temporal** (decisão #9): toda receita em aberto entra, inclusive vencimento
 *   futuro — o FIFO por vencimento (no ResumeBuilder) dita só a ORDEM de consumo;
 * - **escopo estrito por empresa** (decisão #14): `companyId` do repasse;
 * - **`payByBoleto` fica fora** (caminho boleto) — filtro em memória;
 * - compensação `LIQUID` não entra aqui (é assunto do fechamento financeiro, não do repasse);
 * - **as receitas DO PRÓPRIO repasse ficam fora** (`transferId`, mesmo papel do parâmetro
 *   homônimo do {@link getPreviouslyRetained}) — ver abaixo.
 *
 * Duas queries de IGUALDADE por paidStatus (PENDING/PARTIAL_PAID) por bloco de recebedores —
 * igualdade pura é servida por merge dos índices single-field do Firestore, sem índice composto.
 *
 * ⚠️ **Por que excluir o próprio repasse.** O fee do recebedor externo nasce aqui mesmo como
 * `REVENUE`/`NOTA` em aberto (decisão #10) e é oferecido ao cálculo pelo
 * `ResumeBuilder.buildOpenRevenueBuckets`, a partir dos pagamentos EM MEMÓRIA. Uma tentativa de
 * fechamento que grave os itens e falhe depois deixa o fee da tentativa anterior gravado, ativo
 * e em aberto; como o recálculo gera ids novos de pagamento (`oldPayments` nunca sobrevive ao
 * `delete resumes.payments` do TransferEdit), o cálculo seguinte produziria um fee NOVO e esta
 * busca devolveria o ANTIGO — duas fontes para o mesmo dinheiro, abatendo o dobro de
 * `valueBaseTransfer` e, portanto, da base da nota fiscal. Pior: o antigo é deletado logo em
 * seguida pela varredura de órfãos do `beforeSave`, então a `deductions` dele fica na despesa
 * sem contrapartida nenhuma. O filtro é em memória (o `transferId` não entra na query para não
 * exigir índice composto novo) e só descarta receita deste repasse — as de repasses ANTERIORES
 * continuam entrando, que é o comportamento desejado.
 *
 * @param {string[]} receiverIds
 * @param {string} companyId
 * @param {string|null} [transferId] repasse em cálculo — suas próprias receitas são ignoradas
 * @returns {Promise<Object>} mapa negotiatorId → [{id, code, description, dueDateIso,
 *   dateCompetenceIso, saldo}] ordenado em FIFO por vencimento (fallback competência, depois id)
 */
export async function getOpenNotaRevenues(receiverIds, companyId, transferId = null) {
  const makeQuery = (paidStatus) => () => firestore.collection('financialItems')
    .where('active', '==', true)
    .where('companyId', '==', companyId)
    .where('type', '==', FinancialItemsTypes.REVENUE)
    .where('deductionPoint', '==', FinancialDeductionPoint.NOTA)
    .where('paidStatus', '==', paidStatus);

  const [pending, partial] = await Promise.all([
    fcmGetByPropIdsParallel(makeQuery(FinancialItemsPaymentStatus.PENDING), 'negotiator.id', receiverIds),
    fcmGetByPropIdsParallel(makeQuery(FinancialItemsPaymentStatus.PARTIAL_PAID), 'negotiator.id', receiverIds),
  ]);

  const result = {};
  for (const item of [...pending, ...partial]) {
    if (item.payByBoleto) {
      continue;
    }
    // Receita gravada por ESTE repasse (fee de uma tentativa anterior de fechamento): fora — o
    // cálculo já a reoferece pelo caminho em memória, com id novo. Ver o aviso no docstring.
    if (transferId && item.transferId===transferId) {
      continue;
    }
    const saldo = NumberUtils.currency((item.liquid || 0) - (item.paidValue || 0));
    if (saldo <= 0.001) {
      continue;
    }
    (result[item.negotiator?.id] ||= []).push({
      id: item.id,
      code: item.numMov || item.recurrentCode || '',
      description: item.description || item.name || '',
      dueDateIso: item.dueDateIso || '',
      dateCompetenceIso: item.dateCompetenceIso || '',
      saldo: saldo,
    });
  }
  for (const negotiatorId in result) {
    result[negotiatorId].sort((a, b) =>
      String(a.dueDateIso || a.dateCompetenceIso || '').localeCompare(String(b.dueDateIso || b.dateCompetenceIso || ''))
      || String(a.id).localeCompare(String(b.id)));
  }
  return result;
}

/**
 * Todos os valores retidos no mes de competência passado no parâmetro dateCompetenceMonth
 * @param {string[]} receiverIds
 * @param {string} dateCompetenceMonth - format YYYY-MM
 * @param {string} companyId
 * @param {string|null} transferId
 * @param {string|null} financialItemId
 * @param {object[]} taxes
 * @returns {Promise<void>}
 */
export async function getPreviouslyRetained(receiverIds, dateCompetenceMonth, companyId, transferId=null, financialItemId=null, taxes = []) {
  // Fonte ÚNICA de leitura do financeiro novo: financialItems (taxes com retenção NEGATIVA).
  // As coleções legadas (userTransfers/paymentsSplitted) guardam taxes POSITIVOS e NÃO devem
  // ser consultadas aqui — os dados são migrados para financialItems via convertFromOld, que
  // normaliza o sinal. Ver docs/financial/Migracao.md.
  const paymentsP = fcmGetByPropIdsParallel(() => firestore.collection('financialItems')
    .where('active', '==', true)
    .where('companyId', '==', companyId)
    .where('dateCompetenceMonth', '==', dateCompetenceMonth), 'negotiator.id', receiverIds);

  const externalINSSP = fcmGetByPropIdsParallel(() => firestore.collection('externalINSS')
    .where('active', '==', true)
    .where('companyId', '==', companyId)
    .where('type', '==', ExternalINSSType.MONTHLY)
    .where('begin', '==', dateCompetenceMonth), 'negotiator.id', receiverIds);

  const [payments, externalINSS] = await Promise.all([paymentsP, externalINSSP]);

  const result = {};

  // Helper to initialize structure with pf/pj/total buckets.
  // O bucket precisa cobrir TODOS os campos de previouslyRetained que o Tax.js consome
  // (taxes, value, valueBaseTransfer, baseLiquid, valueBeforeReceivedValue) — mesmos campos
  // que o ResumeBuilder.accumulatePreviouslyRetained encadeia entre itens do mesmo repasse.
  const initStructure = () => ({
    pf: { taxes: {}, value: 0, valueBaseIR: 0, liquid: 0, valueBaseTransfer: 0, baseLiquid: 0, valueBeforeReceivedValue: 0 },
    pj: { taxes: {}, value: 0, valueBaseIR: 0, liquid: 0, valueBaseTransfer: 0, baseLiquid: 0, valueBeforeReceivedValue: 0 },
    total: { taxes: {}, value: 0, valueBaseIR: 0, liquid: 0, valueBaseTransfer: 0, baseLiquid: 0, valueBeforeReceivedValue: 0 }
  });

  // Helper to add values to a bucket (pf, pj, or total)
  const addToBucket = (bucket, p) => {
    for (const taxId in p.taxes || {}) {
      bucket.taxes[taxId] = (bucket.taxes[taxId] || 0) + p.taxes[taxId];
    }
    bucket.value += p.value;
    bucket.valueBaseIR += p.valueBaseIR;
    bucket.liquid += p.liquid;
    bucket.valueBaseTransfer += p.valueBaseTransfer;
    bucket.baseLiquid += p.baseLiquid || 0;
    // Item gravado ANTES da etapa BeforeReceivedValue existir não tem o campo: nesse caso o valor
    // após a 1ª etapa É o bruto (não havia imposto ali), então o fallback é `value` — somar 0
    // subestimava o acumulado do mês da etapa `Received`. `??` (e não `||`) preserva o 0
    // legítimo de um item cujo desconto da 1ª etapa consumiu o valor inteiro.
    bucket.valueBeforeReceivedValue += (p.valueBeforeReceivedValue ?? p.value) || 0;
  };

  for (const p of payments) {
    if ((transferId && p.transferId===transferId) || (financialItemId && p.id===financialItemId)) {
      continue;
    }
    // Retenção é conceito de DESPESA. Receita (nota que o cooperado emite contra a cooperativa) e
    // transferências não compõem a progressão mensal de INSS/IRRF do recebedor — o próprio
    // ResumeBuilder.calculateTaxes só encadeia EXPENSE, e o relatório de INSS/IRRF já filtrava
    // assim (inssIrrf.buildInssIrrf, AC2). Sem este filtro, uma receita do mesmo negociador no mês
    // entrava no acumulado e inflava as bases (e os impostos dela viravam "já retido", reduzindo a
    // retenção do repasse seguinte). Filtro em memória de propósito: uma cláusula `type` na query
    // exigiria novo índice composto.
    if (p.type!==FinancialItemsTypes.EXPENSE) {
      continue;
    }

    const negotiatorId = p.negotiator?.id;
    if (!negotiatorId) {
      continue;
    }
    result[negotiatorId] ||= initStructure();

    // Determine if PF or PJ based on negotiator.personType
    const isPJ = p.negotiator?.personType === FinancialPersonType.FINANCIAL_PERSON_LEGAL;
    const bucket = isPJ ? result[negotiatorId].pj : result[negotiatorId].pf;

    addToBucket(bucket, p);
    addToBucket(result[negotiatorId].total, p);
  }

  // externalINSS is always PF (INSS is a PF tax)
  for (const p of externalINSS) {
    const negotiatorId = p.negotiator.id;
    result[negotiatorId] ||= initStructure();

    const INSS_ID = taxes?.find(a=>a.identifier === TaxIdentifier.INSS)?.id;
    const IR_ID = taxes?.find(a=>a.identifier === TaxIdentifier.IR)?.id;

    // Add to PF bucket only.
    // O já-retido é acumulado com sinal NEGATIVO (mesma convenção do addToBucket, que soma
    // p.taxes negativo, e do Tax.js, que faz taxValueMonth + previouslyRetained.taxes). valueINSS
    // e valueIRRF vêm positivos no doc de externalINSS, então subtraímos.
    if (INSS_ID) {
      result[negotiatorId].pf.taxes[INSS_ID] =
        (result[negotiatorId].pf.taxes[INSS_ID] || 0) - NumberUtils.currency(p.valueINSS);
      result[negotiatorId].total.taxes[INSS_ID] =
        (result[negotiatorId].total.taxes[INSS_ID] || 0) - NumberUtils.currency(p.valueINSS);
    }
    if (IR_ID) {
      result[negotiatorId].pf.taxes[IR_ID] =
        (result[negotiatorId].pf.taxes[IR_ID] || 0) - NumberUtils.currency(p.valueIRRF);
      result[negotiatorId].total.taxes[IR_ID] =
        (result[negotiatorId].total.taxes[IR_ID] || 0) - NumberUtils.currency(p.valueIRRF);
    }
    result[negotiatorId].pf.value += NumberUtils.currency(p.value);
    result[negotiatorId].total.value += NumberUtils.currency(p.value);
  }

  return result;
}

/**
 *
 * @param receiverIds
 * @param dateCompetenceMonth
 * @param companyId
 * @returns {Promise<{}>}
 */
export async function getINSSYearlyConfig(receiverIds, dateCompetenceMonth, companyId) {
  const externalINSS = await fcmGetByPropIdsParallel(() => firestore.collection('externalINSS')
    .where('active', '==', true)
    .where('companyId', '==', companyId)
    .where('type', '==', ExternalINSSType.YEARLY)
    .where('begin', '<=', dateCompetenceMonth)
    .where('end', '>=', dateCompetenceMonth), 'negotiator.id', receiverIds);


  const result = {};
  for (const p of externalINSS) {
    result[p.negotiator.id] ||= {
      active: true
    };
  }

  return result;
}


/**
 *
 * @param {string[]} eventsIds
 * @param {Date=} dateBegin
 * @param {Date=} dateEnd
 * @param mode
 * @param actions
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getTransactionsOrphansByEventsIds(eventsIds, dateBegin=null, dateEnd=null, mode=TransferMode.ALL, actions=null) {
  const where = {
    'eventId': {
      'in': eventsIds
    },
    'AND': [
      {
        'cobrancaId': {
          'equals': null
        }
      },
      {
        'transferId': {
          'equals': null
        }
      },
      {
        'canceledId': {
          'equals': null
        }
      },
      {
        'canceledById': {
          'equals': null
        }
      }
    ],
  };
  if(dateBegin) {
    where.AND.push({
      'creationDate': {
        'gte': dateBegin
      }
    });
  }
  if(dateEnd) {
    where.AND.push({
      'creationDate': {
        'lt': dateEnd
      }
    });
  }
  if(actions) {
    where.AND.push({
      'action': {
        'in': actions
      }
    });
  }
  if(mode===TransferMode.RECEIVED) {
    where.AND.push({
      'appealId': {
        'equals': null
      }
    });
  } else if(mode===TransferMode.RECURSED) {
    where.AND.push({
      'appealId': {
        'not': null
      }
    });
  }
  const result = await gqlWithRetry('getTransactionsOrphansByEventsIds', () => apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: where
    },
  }));
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

/**
 * Soma, por evento, o valor das transações JÁ vinculadas a outros repasses (transferId != null,
 * excluindo excludeTransferId — a re-simulação de um repasse não pode contar as próprias
 * transações). Usado pelo cap da flag `notTransferOverToProfessional` (#1074): o distribuído
 * acumulado de um evento não pode passar do produzido, mesmo quando o evento é pago em mais de
 * um repasse. Sem filtro de modo (RECEIVED/RECURSED): vinculada é vinculada, independentemente
 * do modo do repasse que a consumiu. A reversão de repasse zera o transferId, então o mapa se
 * corrige sozinho.
 * @param {string[]} eventsIds
 * @param {string|null} excludeTransferId
 * @returns {Promise<Object<string, number>>} mapa {eventId: soma repassada}
 */
export async function getPreviouslyTransferredByEventsIds(eventsIds, excludeTransferId=null) {
  if(!eventsIds?.length) {
    return {};
  }
  const where = {
    'eventId': {
      'in': eventsIds
    },
    'AND': [
      {
        'transferId': {
          'not': null
        }
      },
      {
        'canceledId': {
          'equals': null
        }
      },
      {
        'canceledById': {
          'equals': null
        }
      },
      {
        'action': {
          'in': [BillingActions.Received, BillingActions.ReceivedFromOver]
        }
      }
    ],
  };
  // Select enxuto (não o TransactionFragment ~39 campos): esta query roda a cada recálculo do
  // repasse (makeResumesInner) com `eventId in [...]` sobre TODOS os eventos — em repasses grandes
  // seria payload pesado à toa. Só precisamos de eventId/value/transferId para somar por evento e
  // excluir o repasse atual. O apolloClient é `no-cache` (ver apolloClient.js), então o select
  // parcial não gera warning de normalização nem interfere no cache de outras queries.
  const result = await gqlWithRetry('getPreviouslyTransferredByEventsIds', () => apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          eventId
          value
          transferId
        }
      }
    `,
    variables: {
      where: where
    },
  }));
  const map = {};
  for (const tr of result.data.transactions) {
    if (excludeTransferId && tr.transferId===excludeTransferId) {
      continue;
    }
    if (!tr.eventId) {
      continue;
    }
    map[tr.eventId] = NumberUtils.currency((map[tr.eventId] || 0) + (tr.value || 0));
  }
  return map;
}

/**
 *
 * @param {string|Object=} arg
 * @param {string} [arg.companyId]
 * @param {string} [arg.matrizId]
 * @param [arg.dateBegin]
 * @param [arg.dateEnd]
 * @param {string[]} [arg.healthInsuranceIds =[]]
 * @param {string[]} [arg.anesthetistIds =[]]
 * @param {string[]} [arg.batchesIds =[]]
 * @param {string[]} [arg.cobrancaIds =[]]
 * @param {boolean} [arg.byDate]
 * @param {string []} [arg.actions]
 * @param {string []} [arg.financialItemIds]
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getActiveTransactions({ companyId, matrizId, dateBegin, dateEnd,
  healthInsuranceIds = [], anesthetistIds = [],
  batchesIds = [], cobrancaIds = [],
  byDate = false, actions = [], eventsIds =[], financialItemIds = []} = {}) {
  let _dateBegin = null;
  let _dateEnd = null;
  if(typeof dateBegin?.toDate==='function') {
    _dateBegin = dateBegin.startOf('day').toDate();
  }
  if(typeof dateEnd?.toDate==='function') {
    _dateEnd = dateEnd.endOf('day').toDate();
  }
  const where = {
    'AND': [
      {
        'canceledId': {
          'equals': null
        }
      },
      {
        'canceledById': {
          'equals': null
        }
      }
    ],
  };
  if(_dateBegin) {
    if (byDate) {
      where.AND.push({
        'date': {
          'gte': _dateBegin
        }
      });
    } else {
      where.AND.push({
        'creationDate': {
          'gte': _dateBegin
        }
      });
    }
  }
  if(_dateEnd) {
    if (byDate) {
      where.AND.push({
        'date': {
          'lt': _dateEnd
        }
      });
    } else {
      where.AND.push({
        'creationDate': {
          'lt': _dateEnd
        }
      });
    }
  }

  if(companyId) {
    where.AND.push({
      'companyId': {
        'equals': companyId
      }
    });
  }
  if(matrizId) {
    where.AND.push({
      'matrizId': {
        'equals': matrizId
      }
    });
  }
  if(actions?.length > 0) {
    where.AND.push({
      'action': {
        'in': actions
      }
    });
  }
  if(eventsIds?.length > 0) {
    where.AND.push({
      'eventId': {
        'in': eventsIds
      }
    });
  }
  if(healthInsuranceIds?.length) {
    where.AND.push({
      'healthInsuranceId': {
        'in': healthInsuranceIds
      }
    });
  }
  if (anesthetistIds?.length) {
    where.AND.push({
      'receiverId': {
        'in': anesthetistIds
      }
    });
  }
  if (batchesIds?.length) {
    where.AND.push({
      'batchesIds': {
        'hasSome': batchesIds
      }
    });
  }
  if(cobrancaIds?.length) {
    where.AND.push({
      'cobrancaId': {
        'in': cobrancaIds
      }
    });
  }
  if(financialItemIds?.length) {
    where.AND.push({
      'financialItemId': {
        'in': financialItemIds
      }
    });
  }
  // console.log('where: ', where);

  const result = await apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: where
    },
  });
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

/**
 *
 * @param {Object=} where
 * @returns {Promise<TransactionEntity[]>}
 */
export async function getTransactionsByWhere(where) {
  console.log('where: ', where);

  const result = await apolloClient.query({
    query: gql`
      query ($where: TransactionWhereInput) {
        transactions(where: $where) {
          ...TransactionFragment
        }
      }
      ${TransactionFragment}
    `,
    variables: {
      where: where
    },
  });
  return result.data.transactions.map((a)=>new TransactionEntity(a));
}

/**
 * Grava/atualiza transações em massa no Postgres, FATIADO em chunks com retry.
 *
 * O upsert único de TODAS as transações de um fechamento grande passava do timeout do
 * proxy (~60s): o cliente recebia 500 sem header CORS (ERR_FAILED) e abortava — enquanto
 * o servidor podia seguir processando e commitar, deixando transações vinculadas sem o
 * repasse fechado. O RetryLink do apolloClient repetia, mas repetir a MESMA requisição
 * gigante falha de novo. Fatiado, cada chamada fica curta (bem abaixo do timeout) e o
 * gqlWithRetry cobre 500 transitório com backoff (1s/3s/8s).
 *
 * Cada chunk é um upsert atômico e idempotente — repetir é seguro; falha definitiva no
 * meio deixa chunks parciais vinculados, que o fechamento tolera (resgate no
 * makeResumesInner + varredura de reconciliação no beforeSave).
 *
 * @param {[TransactionEntity]} trs
 * @param {Number=} chunkSize
 * @param {{delays?: Number[]}=} retryOptions - repassado ao gqlWithRetry (testes)
 * @returns {Promise<*|*[]>}
 */
export async function saveTransactions(trs, chunkSize = 200, retryOptions = undefined) {
  const saved = [];
  for (let i = 0; i < (trs?.length || 0); i += chunkSize) {
    const chunk = trs.slice(i, i + chunkSize);
    const p = await gqlWithRetry(`saveTransactions ${i + chunk.length}/${trs.length}`, () => apolloClient.mutate({
      // Query
      mutation: gql`
        mutation ($data: [UpsertOneTransactionInput!]!) {
          upsertManyTransactionInTransaction(data: $data) {
            id
          }
        }
      `,
      // Parameters
      variables: {
        data: chunk.map(a=>a.getFullMutation())
      },
      // Margem sobre o timeout global (60s) — o timeoutLink fica por fora do RetryLink,
      // então este valor cobre a cadeia de tentativas do link para o chunk.
      context: {
        timeout: 120000,
      },
    }), retryOptions);
    saved.push(...(p?.data?.upsertManyTransactionInTransaction || []));
  }

  return saved;
}



/**
 * Vincula transações a um repasse no Postgres gravando SOMENTE `transferId` e `paid`.
 *
 * É o caminho rápido do fechamento, no lugar do {@link saveTransactions}. A diferença é de
 * ordem de grandeza, não de ajuste fino:
 *
 * - **`saveTransactions`** manda `upsertManyTransactionInTransaction`, e o resolver do volanpg
 *   (`CustomTransactionResolver`) monta **um `prisma.upsert` por transação** dentro de um
 *   `$transaction`. São N statements sequenciais segurando a mesma conexão do pool — com
 *   milhares de transações num fechamento, é onde o repasse gastava a maior parte dos 30s+.
 *   Some-se o payload: o `getFullMutation` serializa **todas** as ~35 colunas em `create` +
 *   `update` (ver `docs/fcmPerformance.md`, seção 5.1, no volanpg), então o browser sobe alguns
 *   MB para mudar dois campos.
 * - **Aqui**, `updateManyTransaction` vira **um único `UPDATE ... WHERE id IN (...)`** por
 *   chunk, e o payload é a lista de ids + dois valores.
 *
 * Fidelidade: no fechamento **todas** as transações recebem os mesmos dois valores
 * (`TransferEdit.lockTransfer` faz `tr.transferId = valueId; tr.paid = true` no lote inteiro),
 * que é exatamente a pré-condição do `updateMany`. As transações vêm das queries do próprio
 * Postgres (open/orphans/byTransferId), então a linha sempre existe e o `create` do upsert era
 * caminho morto — mas, se o `count` devolvido não bater com o tamanho do chunk (linha apagada
 * no meio do caminho), cai no **fallback** para o upsert daquele chunk, preservando o
 * comportamento antigo em vez de vincular menos transações em silêncio.
 *
 * Idempotente (regravar os mesmos valores é no-op semântico), então o `gqlWithRetry` pode
 * repetir com segurança, como no `saveTransactions`.
 *
 * @param {[TransactionEntity]} trs transações a vincular (só os ids são enviados)
 * @param {String} transferId id do repasse
 * @param {{paid?: Boolean, chunkSize?: Number, retryOptions?: {delays?: Number[]}}=} options
 * @returns {Promise<{updated: Number, upserted: Number}>} quantas linhas o UPDATE atingiu e
 *   quantas precisaram do fallback via upsert
 */
export async function linkTransactionsToTransfer(trs, transferId, {paid = true, chunkSize = 1000, retryOptions = undefined} = {}) {
  // Dedup por id: o `in` do Prisma casa uma linha por id, então um id repetido na entrada faz
  // `count < chunk.length` e dispara o upsert de fallback — até 1000 linhas reescritas uma a
  // uma por uma divergência que não existe. O fallback continua valendo para o caso real
  // (linha apagada no Postgres entre a leitura e o fechamento).
  const seenIds = new Set();
  const list = (trs || []).filter((a)=>{
    if (!a?.id || seenIds.has(a.id)) {
      return false;
    }
    seenIds.add(a.id);
    return true;
  });
  let updated = 0;
  let upserted = 0;
  for (let i = 0; i < list.length; i += chunkSize) {
    const chunk = list.slice(i, i + chunkSize);
    const ids = chunk.map((a)=>a.id);
    const p = await gqlWithRetry(`linkTransactionsToTransfer ${i + chunk.length}/${list.length}`, () => apolloClient.mutate({
      mutation: gql`
        mutation ($data: TransactionUpdateManyMutationInput!, $where: TransactionWhereInput) {
          updateManyTransaction(data: $data, where: $where) {
            count
          }
        }
      `,
      variables: {
        data: {
          transferId: {set: transferId || null},
          paid: {set: !!paid},
        },
        where: {
          id: {
            in: ids
          }
        }
      },
      // Mesma margem do saveTransactions: o timeoutLink fica por fora do RetryLink, então este
      // valor cobre a cadeia de tentativas do link para o chunk.
      context: {
        timeout: 120000,
      },
    }), retryOptions);
    const count = p?.data?.updateManyTransaction?.count ?? 0;
    updated += count;
    if (count!==chunk.length) {
      // Linha ausente no Postgres (apagada entre a leitura e o fechamento). O upsert recria,
      // como o fluxo antigo fazia — vincular menos transações do que o cálculo previu deixaria
      // o repasse fechado sem parte das fontes marcadas como pagas.
      console.warn('linkTransactionsToTransfer: update atingiu menos linhas que o chunk, aplicando upsert de fallback',
        {esperado: chunk.length, atualizado: count});
      await saveTransactions(chunk, 200, retryOptions);
      upserted += chunk.length;
    }
  }
  return {updated, upserted};
}

export async function updateTransactionsPartialByEventId(eventId, data) {
  const p = await apolloClient.mutate({
    // Query
    mutation: gql`
      mutation ($data: TransactionUpdateManyMutationInput!, $where: TransactionWhereInput) {
        updateManyTransaction(data: $data, where: $where) {
          count
        }
      }
    `,
    // Parameters
    variables: {
      data: TransactionEntity.getMutation(data),
      where: {
        eventId: {
          equals: eventId
        }
      }
    },
  });

  return p?.data?.upsertManyTransactionInTransaction || [];
}


export async function removeTr(trId) {
  if(!trId) {
    return false;
  }
  await apolloClient.mutate({
    // Query
    mutation: gql`
      mutation DeleteOneTransaction($where: TransactionWhereUniqueInput!) {
        deleteOneTransaction(where: $where) {
          id
        }
      }
    `,
    // Parameters
    variables: {
      where: {
        id: trId
      }
    },
  });

  await firestore.collection('transactions').doc(trId).delete();
  return true;
}

/**
 * @param trs
 * @param events
 * @returns {Promise<number>}
 */
export async function saveTransactionsAndEvents(trs, events) {
  await saveTransactions(trs); // se acontecer algum erro, não gravar eventos

  await firestore.collection('events').fcmSliceUpdate(events);
  return 0;
}

/**
 *
 * @param {String[]} ids
 * @returns {Promise<any[]>}
 */
export async function getCobrancasByIds(ids) {
  const result = [];
  for(let i=0;i<ids?.length;i = i+10) {
    const ids10 = ids.slice(i, i+10);
    const trs = await firestore.collection('cobrancas')
      .where('active', '==', true)
      .where(FieldPath.documentId(), 'in', ids10)
      .get();
    for(const trObj of trs.docs) {
      result.push({
        ...trObj.data(),
        id: trObj.id
      });
    }
  }
  return result;
}


export async function saveUserTransfers(transfers) {
  const batch = firestore.batch();
  for(const tr of transfers) {
    await batch.set(firestore.collection('userTransfers').doc(tr.id), {...tr}, {merge: false});
  }
  await batch.commit();
  return 0;
}

export async function deleteUserTransfers(transfersIds) {
  const batch = firestore.batch();
  for(const trId of transfersIds) {
    await batch.delete(firestore.collection('userTransfers').doc(trId));
  }
  await batch.commit();
  return 0;
}

export async function getCobrancas(input) {
//  console.log('getPatientsCpf', input);
  const dateBegin = moment(input.dateMonthSelected).startOf('month');
  const dateEnd = moment(input.dateMonthSelected).endOf('month');
  let ref = firestore.collection('cobrancas')
    .where('active', '==', true)
    .where('isAllowTransfer', '==', true)
    .where('companyId', '==', input.companyId)
    .where('date', '>=', dateBegin.toDate())
    .where('date', '<=', dateEnd.toDate())
    .orderBy('date', 'desc');

  if(input.healthInsuranceId) {
    ref = ref.where('healthInsuranceId', '==', input.healthInsuranceId);
  }

  if(input.size) {
    ref = ref.limit(input.size || 100);
  }
  else {
    ref = ref.limit(100);
  }

  const objects = await ref.get();
  const events = [];
  objects.forEach((obj)=>{
    const tmp = obj.data();
    tmp.id = obj.id;
    tmp.dateShow = '';
    const dateTmp = moment(typeof tmp.date.toDate==='function'?tmp.date.toDate():tmp.dateIso);
    if(dateTmp.isValid()) {
      tmp.dateShow = dateTmp.format(input.dateFormat || 'DD/MM/YYYY');
    }
    events.push(tmp);
  });

  return {
    hits: events,
    total: events.length,
  };
}


export async function getTransactionsFetched(input) {
  console.warn('TODO, ainda não implementado getTransactionsFetched no GQL', input);

  return {
    hits: [],
    total: 0,
  };
}



/**
 *
 * @param financialItemId
 */
export async function getTransactionsAggregateByfinancialItemId(financialItemId) {
  if(!financialItemId) {
    console.error('financialItemId not found');
    return [];
  }
  const result = await apolloClient.query({
    query: gql`
      query($where: TransactionWhereInput) {
        aggregateTransaction(where: $where) {
          _count {
            eventId
          }
          _sum {
            value
          }
        }
      }
    `,
    variables: {
      where: {
        'financialItemId': {
          'equals': financialItemId
        },
        'AND': [
          {
            'action': {
              'in': [BillingActions.Received, BillingActions.ReceivedToOver]
            }
          },
        ]
      }
    },
  });
  console.log('result.data', result.data?.aggregateTransaction);
  return result.data?.aggregateTransaction || null;
}









